/**
 * sentry.ts — Initialisation Sentry (monitoring erreurs)
 *
 * Capture les erreurs cote client (React) et envoie les traces a Sentry.
 * Les erreurs cote serveur (Edge Functions Supabase) sont hors scope de ce fichier.
 *
 * Variables d'environnement requises (prefixe VITE_ car expose au navigateur) :
 *  - VITE_SENTRY_DSN         : DSN du projet Sentry (obligatoire pour activer)
 *  - VITE_SENTRY_ENVIRONMENT : "development" | "preview" | "production" (optionnel)
 *  - VITE_SENTRY_RELEASE     : identifiant de release (optionnel, ex. commit SHA)
 *
 * Si VITE_SENTRY_DSN est absent, l'init est silencieusement ignoree — l'app continue
 * de fonctionner sans monitoring (utile en dev local).
 */

import * as Sentry from "@sentry/react";
import posthog from "posthog-js";

let initialized = false;

export function initSentry(): void {
  if (initialized) return;

  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) {
    // Pas de DSN → on desactive Sentry silencieusement
    return;
  }

  const environment =
    (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ??
    (import.meta.env.MODE as string);
  const release = import.meta.env.VITE_SENTRY_RELEASE as string | undefined;

  Sentry.init({
    dsn,
    environment,
    release,

    // Performance monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Echantillonnage traces performance : 100% en dev, 10% en prod
    tracesSampleRate: environment === "production" ? 0.1 : 1.0,

    // Session Replay : 10% des sessions normales + 100% des sessions avec erreur
    replaysSessionSampleRate: environment === "production" ? 0.1 : 0,
    replaysOnErrorSampleRate: 1.0,

    // Filtrage : on ignore les erreurs connues et non-actionables
    ignoreErrors: [
      // Extensions navigateur
      "top.GLOBALS",
      // Erreurs reseau ponctuelles
      "Network request failed",
      "NetworkError",
      "Failed to fetch",
      // ResizeObserver bruit
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      // Supabase Navigator Locks : aborts benins quand la page navigue / autre onglet tient le lock
      "AbortError",
      "signal is aborted without reason",
      "signal is aborted",
    ],

    // Injecte le lien vers le session replay PostHog dans chaque event Sentry.
    // Resultat : chaque alerte Sentry contient un tag "PostHog Session URL"
    // cliquable qui ouvre le replay au moment exact de l'erreur.
    beforeSend(event) {
      try {
        if (posthog.__loaded) {
          const sessionId = posthog.get_session_id?.();
          const replayUrl = posthog.get_session_replay_url?.({
            withTimestamp: true,
            timestampLookBack: 30,
          });
          if (sessionId) {
            event.tags = {
              ...event.tags,
              "PostHog Session ID": sessionId,
            };
          }
          if (replayUrl) {
            event.tags = {
              ...event.tags,
              "PostHog Session URL": replayUrl,
            };
          }
        }
      } catch {
        // Ne jamais bloquer l'envoi Sentry a cause d'une erreur PostHog
      }
      return event;
    },
  });

  initialized = true;
}

// Re-export pour centraliser les imports ailleurs dans l'app
export { Sentry };
